/**
 * @module app.core
 */

import { autoinject, TaskQueue, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { defer } from 'app/shared/lib/decorators';
import { MemberService } from 'app/cd/services/member-service';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import * as CD from 'app/cd/models/essette-api';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { validate } from 'app/shared/elements/ess-rule';
import { deepClone } from 'app/shared/lib/utilities';

@autoinject
export class memberProviders extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberId: string | null = null;

    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;

    protected member: CD.Member = {};
    protected memberPlans: ReadonlyArray<CD.MemberPlan> = [];
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    public pageSize: number = 10;
    protected rememberExpand: boolean = true;
    public showInactiveToggle: boolean = false;

    protected editedProvider: CD.MemberProvider | null = null;
    protected validate = validate;
    protected $isValid: boolean = false;

    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CD.MemberPlan[]>;
    protected $pager!: EssPagerCustomElement;
    private $memberProviderModal!: IPopup;
    protected $providerForm!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly queue: TaskQueue,
        ...rest: any[]) {
        super(...rest);

        this.checkPermissions();

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'eligibility.rank',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected async bind(): Promise<void> {
        if(!this.memberId) { this.logger.warn('Bindable property [member-id] was not set at binding'); }

        this.checkPermissions();
        this.deferReload();
    }

    ////****************** Events ***************************************************////
    protected handleMemberProviderSaved(event: FormSuccessCustomEvent) {
        this.$memberProviderModal.close();

        if(event.detail.form.reset) {
            event.detail.form.reset();
        }

        this.deferReload();
    }

    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadMember();
        await this.loadMemberPlans();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    @defer
    protected deferReload() {
        this.reload();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
            if(this.sorting.entries.length == 0) {
                this.sorting.entries = [
                    {
                        reference: 'eligibility.rank',
                        direction: SortDirection.Ascending
                    }
                ];
            }
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    private restoreExpanded() {
        if(!this.rememberExpand) { return; }
        // we need to give aurelia a chance to bind the providers into the
        // table before we ask the table to expand the rows
        this.queue.queueTask(() => {
            this.$table.restoreExpanded(this.$table.expanded as Array<CD.MemberPlan>);
        });
    }

    protected openMemberProviderModal(provider: CD.MemberProvider | null): void {
        this.editedProvider = provider ? deepClone(provider) : null;
        this.$memberProviderModal.open();
    }

    protected closeMemberProviderModal() {
        if(this.$providerForm.reset) this.$providerForm.reset();
        this.$memberProviderModal.close();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMember(): Promise<void> {
        if(!this.memberId) return;

        const includes: string[] = [
            'primaryCarePhysician',
            'primaryCarePhysician.location'
        ];

        const fields: string[] = [
            'primaryCarePhysician.location.$display',
            'primaryCarePhysician.$display'
        ];

        try {
            this.state.beginLoad();
            let response = await this.memberService.loadMember(this.memberId, includes, fields);
            this.member = response.results[0];
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadMemberPlans(): Promise<void> {
        if(!this.memberId) { return; }

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: this.showInactiveToggle
        };

        let includes = [
            'healthPlan',
            'lineOfBusiness',
            'member'
        ];

        let fields = [
            'healthPlan.description',
            'lineOfBusiness.$display',
            'identifier.primary',
            'isPrimary',
            'eligibility.rank'
        ];

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadPlansByMember(this.memberId, options, includes, fields);
            this.memberPlans = response.results;
            this.resultCount = response.count || 0;
            this.restoreExpanded();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
